import { useState } from 'react';
import {
  useAdminUserCounts,
  useAdminTeamCounts,
  useRevenueStats,
  useTrialStats,
  useChannelHealthStats,
  useChurnStats,
  useMaintenanceStatus,
  useSetMaintenanceMode,
} from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Link } from 'react-router-dom';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';
import { Card, Icon } from '@/components/ui';

/**
 * AdminDashboard Page
 *
 * Comprehensive admin dashboard with revenue analytics, trial tracking,
 * channel health monitoring, churn stats, and maintenance mode.
 */

/** Semantic accent color keys for stat card icons/borders (Tailwind tokens, no inline hex). */
type AccentColor = 'blue' | 'green' | 'purple' | 'primary' | 'red' | 'orange' | 'crimson' | 'coral';

const HERO_ACCENT_BG: Record<AccentColor, string> = {
  blue: 'bg-primary-700',
  green: 'bg-emerald-500',
  purple: 'bg-secondary-500',
  primary: 'bg-primary-500',
  red: 'bg-red-500',
  orange: 'bg-amber-500',
  crimson: 'bg-red-600',
  // Original hero accent — matches the legacy inline #ff5757 exactly (not a Tailwind palette shade).
  coral: 'bg-[#ff5757]',
};

const STAT_ACCENT_CLASSES: Record<AccentColor, { border: string; bg: string }> = {
  blue: { border: 'border-l-primary-700', bg: 'bg-primary-700' },
  green: { border: 'border-l-emerald-500', bg: 'bg-emerald-500' },
  purple: { border: 'border-l-secondary-500', bg: 'bg-secondary-500' },
  primary: { border: 'border-l-primary-500', bg: 'bg-primary-500' },
  red: { border: 'border-l-red-500', bg: 'bg-red-500' },
  orange: { border: 'border-l-amber-500', bg: 'bg-amber-500' },
  crimson: { border: 'border-l-red-600', bg: 'bg-red-600' },
  coral: { border: 'border-l-[#ff5757]', bg: 'bg-[#ff5757]' },
};

interface HeroStatCardProps {
  title: string;
  value: string | number;
  icon: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  accentColor?: AccentColor;
  isLoading?: boolean;
}

const HeroStatCard = ({
  title,
  value,
  icon,
  trend,
  accentColor = 'coral',
  isLoading
}: HeroStatCardProps) => (
  <Card padding="md" className="hover:shadow-md transition-shadow">
    {isLoading ? (
      <div className="h-24 flex items-center justify-center">
        <LoadingSpinner size="sm" />
      </div>
    ) : (
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl ${HERO_ACCENT_BG[accentColor]}`}
            >
              <Icon name={icon} size="custom" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{title}</p>
              <h3 className="text-3xl font-bold text-gray-900 mt-1">{value}</h3>
            </div>
          </div>
          {trend && (
            <div className={`mt-3 flex items-center gap-1 text-sm font-medium ${
              trend.isPositive ? 'text-green-600' : 'text-red-600'
            }`}>
              <Icon name={trend.isPositive ? ICONS.ARROW_UP : ICONS.ARROW_DOWN} size="custom" />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </Card>
);

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  borderColor: AccentColor;
  isLoading?: boolean;
}

const StatCard = ({ title, value, icon, borderColor, isLoading }: StatCardProps) => (
  <Card padding="md" className={`border-l-4 ${STAT_ACCENT_CLASSES[borderColor].border}`}>
    {isLoading ? (
      <div className="h-20 flex items-center justify-center">
        <LoadingSpinner size="sm" />
      </div>
    ) : (
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-xl ${STAT_ACCENT_CLASSES[borderColor].bg}`}
        >
          <Icon name={icon} size="custom" />
        </div>
      </div>
    )}
  </Card>
);

interface QuickActionCardProps {
  to: string;
  title: string;
  description: string;
  icon: string;
  iconColor: string;
}

const QuickActionCard = ({ to, title, description, icon, iconColor }: QuickActionCardProps) => (
  <Link
    to={to}
    className="group bg-white p-6 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 border border-gray-100"
  >
    <div className="flex items-start justify-between mb-3">
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl"
        style={{ backgroundColor: iconColor }}
      >
        <Icon name={icon} size="custom" />
      </div>
      <Icon
        name={ICONS.ARROW_RIGHT}
        size="custom"
        className="text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all"
      />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500">{description}</p>
  </Link>
);

export const AdminDashboard = () => {
  const { formatDateTime } = useTimezone();
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Fetch data
  const { data: userCounts, isLoading: usersLoading, isError: usersError, error: usersErrorObj } = useAdminUserCounts();
  const { data: teamCounts, isLoading: teamsLoading, isError: teamsError, error: teamsErrorObj } = useAdminTeamCounts();
  const { data: revenueStats, isLoading: revenueLoading, isError: revenueIsError, error: revenueErrorObj } = useRevenueStats();
  const { data: trialStats, isLoading: trialsLoading, isError: trialsError, error: trialsErrorObj } = useTrialStats();
  const { data: channelHealth, isLoading: channelHealthLoading, isError: channelHealthError, error: channelHealthErrorObj } = useChannelHealthStats();
  const { data: churnStats, isLoading: churnLoading, isError: churnIsError, error: churnErrorObj } = useChurnStats();
  const { data: maintenanceStatus, isLoading: maintenanceLoading, isError: maintenanceError, error: maintenanceErrorObj } = useMaintenanceStatus();

  const setMaintenanceMode = useSetMaintenanceMode();

  const failedQueries: { label: string; error: unknown }[] = [
    ...(usersError ? [{ label: 'User counts', error: usersErrorObj }] : []),
    ...(teamsError ? [{ label: 'Team counts', error: teamsErrorObj }] : []),
    ...(revenueIsError ? [{ label: 'Revenue analytics', error: revenueErrorObj }] : []),
    ...(trialsError ? [{ label: 'Trial tracking', error: trialsErrorObj }] : []),
    ...(channelHealthError ? [{ label: 'Channel health', error: channelHealthErrorObj }] : []),
    ...(churnIsError ? [{ label: 'Churn analytics', error: churnErrorObj }] : []),
    ...(maintenanceError ? [{ label: 'Maintenance mode', error: maintenanceErrorObj }] : []),
  ];

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleMaintenanceToggle = async (enabled: boolean) => {
    try {
      await setMaintenanceMode.mutateAsync({
        enabled,
        message: enabled ? maintenanceMessage || undefined : undefined,
      });
      toast.success(enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
      if (!enabled) {
        setMaintenanceMessage('');
      }
    } catch (error) {
      toast.error('Failed to update maintenance mode');
    }
  };

  return (
    <div className="space-y-8">
      {/* Error Banner */}
      {failedQueries.length > 0 && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          <p className="font-medium">
            {failedQueries.length === 1
              ? `Failed to load ${failedQueries[0].label}: ${formatApiError(failedQueries[0].error).message}`
              : 'Some dashboard data failed to load. Please refresh.'}
          </p>
          {failedQueries.length > 1 && (
            <ul className="list-disc list-inside mt-2 text-sm">
              {failedQueries.map(({ label, error }) => (
                <li key={label}>
                  {label}: {formatApiError(error).message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Welcome Section */}
      <div className="bg-gradient-mint rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-gray-900">Welcome to Admin Dashboard</h1>
        <p className="text-gray-700 text-lg">{today}</p>
        <p className="text-gray-600 mt-2">Monitor system performance and manage platform operations</p>
      </div>

      {/* Hero Stats */}
      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <HeroStatCard
            title="Total Users"
            value={userCounts?.total ?? 0}
            icon={ICONS.USER}
            accentColor="blue"
            isLoading={usersLoading}
          />
          <HeroStatCard
            title="Active Teams"
            value={teamCounts?.active ?? 0}
            icon={ICONS.TEAM}
            accentColor="green"
            isLoading={teamsLoading}
          />
          <HeroStatCard
            title="Super Admins"
            value={userCounts?.superAdmins ?? 0}
            icon={ICONS.CROWN}
            accentColor="purple"
            isLoading={usersLoading}
          />
          <HeroStatCard
            title="Trial Teams"
            value={teamCounts?.trials ?? 0}
            icon={ICONS.CLOCK}
            accentColor="coral"
            isLoading={teamsLoading}
          />
        </div>
      </section>

      {/* Revenue Section */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Revenue Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <StatCard
            title="Monthly Recurring Revenue (MRR)"
            value={revenueLoading ? '...' : `$${revenueStats?.mrr.toLocaleString() ?? 0}`}
            icon={ICONS.WALLET}
            borderColor="green"
            isLoading={revenueLoading}
          />
          <StatCard
            title="Annual Recurring Revenue (ARR)"
            value={revenueLoading ? '...' : `$${revenueStats?.arr.toLocaleString() ?? 0}`}
            icon={ICONS.ANALYTICS}
            borderColor="blue"
            isLoading={revenueLoading}
          />
          <StatCard
            title="Paying Teams"
            value={revenueStats?.payingTeamsCount ?? 0}
            icon={ICONS.TEAM}
            borderColor="purple"
            isLoading={revenueLoading}
          />
        </div>

        {/* Revenue by Package */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Revenue by Package</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Package</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Teams</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">MRR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {revenueLoading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-center">
                      <LoadingSpinner size="sm" />
                    </td>
                  </tr>
                ) : revenueStats?.byPackage && revenueStats.byPackage.length > 0 ? (
                  revenueStats.byPackage.map((pkg) => (
                    <tr key={pkg.packageId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{pkg.packageName}</td>
                      <td className="px-6 py-4 text-right text-gray-700">{pkg.teamCount}</td>
                      <td className="px-6 py-4 text-right font-semibold text-green-600">
                        ${pkg.mrr.toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-center text-gray-600">
                      No revenue data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Trials Section */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Trial Tracking</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Active Trials"
            value={trialStats?.activeTrials ?? 0}
            icon={ICONS.CLOCK}
            borderColor="blue"
            isLoading={trialsLoading}
          />
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-yellow-500">
            {trialsLoading ? (
              <div className="h-20 flex items-center justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={ICONS.CAUTION} size="custom" className="text-yellow-600 text-xl" />
                  <p className="text-sm font-medium text-gray-500">Expiring Soon (7 days)</p>
                </div>
                <p className="text-3xl font-bold text-yellow-600">{trialStats?.expiringIn7Days ?? 0}</p>
              </>
            )}
          </div>
          <StatCard
            title="Conversions (30d)"
            value={trialStats?.conversionsLast30Days ?? 0}
            icon={ICONS.CHECK}
            borderColor="green"
            isLoading={trialsLoading}
          />
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-primary-700">
            {trialsLoading ? (
              <div className="h-20 flex items-center justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-500 mb-1">Conversion Rate</p>
                <p className="text-3xl font-bold text-primary-800">
                  {trialStats?.conversionRate ? `${trialStats.conversionRate.toFixed(1)}%` : '0%'}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Channel Health Section */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Channel Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <StatCard
            title="Total Channels"
            value={channelHealth?.totalChannels ?? 0}
            icon={ICONS.LINK}
            borderColor="blue"
            isLoading={channelHealthLoading}
          />
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
            {channelHealthLoading ? (
              <div className="h-20 flex items-center justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={ICONS.CROSS} size="custom" className="text-red-600 text-xl" />
                  <p className="text-sm font-medium text-gray-500">Expired Tokens</p>
                </div>
                <p className="text-3xl font-bold text-red-600">{channelHealth?.expiredTokens ?? 0}</p>
              </>
            )}
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-orange-500">
            {channelHealthLoading ? (
              <div className="h-20 flex items-center justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={ICONS.REFRESH} size="custom" className="text-orange-600 text-xl" />
                  <p className="text-sm font-medium text-gray-500">Needs Reconnection</p>
                </div>
                <p className="text-3xl font-bold text-orange-600">{channelHealth?.needsReconnection ?? 0}</p>
              </>
            )}
          </div>
        </div>

        {/* Channel Health by Platform */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Channels by Platform</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Platform</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Total</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Expired</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Health Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {channelHealthLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <LoadingSpinner size="sm" />
                    </td>
                  </tr>
                ) : channelHealth?.byPlatform && channelHealth.byPlatform.length > 0 ? (
                  channelHealth.byPlatform.map((platform) => {
                    const healthRate = platform.total > 0
                      ? ((platform.total - platform.expired) / platform.total * 100).toFixed(1)
                      : '0';
                    return (
                      <tr key={platform.platform} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{platform.platform}</td>
                        <td className="px-6 py-4 text-right text-gray-700">{platform.total}</td>
                        <td className="px-6 py-4 text-right text-red-600 font-semibold">{platform.expired}</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`font-semibold ${
                            parseFloat(healthRate) >= 90
                              ? 'text-green-600'
                              : parseFloat(healthRate) >= 70
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}>
                            {healthRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-600">
                      No channel data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Churn Section */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Churn Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
          <StatCard
            title="Cancelled (30d)"
            value={churnStats?.cancelledLast30Days ?? 0}
            icon={ICONS.CROSS}
            borderColor="red"
            isLoading={churnLoading}
          />
          <StatCard
            title="Expired (30d)"
            value={churnStats?.expiredLast30Days ?? 0}
            icon={ICONS.ALARM_CLOCK}
            borderColor="orange"
            isLoading={churnLoading}
          />
          <StatCard
            title="Total Churned"
            value={churnStats?.totalChurned ?? 0}
            icon={ICONS.CAUTION}
            borderColor="crimson"
            isLoading={churnLoading}
          />
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-red-500">
            {churnLoading ? (
              <div className="h-20 flex items-center justify-center">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-500 mb-1">Churn Rate</p>
                <p className="text-3xl font-bold text-red-600">
                  {churnStats?.churnRate ? `${churnStats.churnRate.toFixed(1)}%` : '0%'}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Churn by Package */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Churn by Package</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700">Package</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700">Churned Teams</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {churnLoading ? (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center">
                      <LoadingSpinner size="sm" />
                    </td>
                  </tr>
                ) : churnStats?.byPackage && churnStats.byPackage.length > 0 ? (
                  churnStats.byPackage.map((pkg) => (
                    <tr key={pkg.packageId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{pkg.packageName}</td>
                      <td className="px-6 py-4 text-right text-red-600 font-semibold">{pkg.churned}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-gray-600">
                      No churn data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Maintenance Mode Section */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Maintenance Mode</h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {maintenanceLoading ? (
            <div className="py-8 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">System Maintenance Mode</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {maintenanceStatus?.enabled
                      ? 'System is currently in maintenance mode'
                      : 'System is operating normally'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maintenanceStatus?.enabled ?? false}
                    onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                    disabled={setMaintenanceMode.isPending}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:border-2 peer-focus:border-primary-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {maintenanceStatus?.enabled && (
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Icon name={ICONS.CAUTION} size="custom" className="text-yellow-600 text-xl mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-900">
                        Maintenance Mode Active
                      </p>
                      <p className="text-sm text-yellow-800 mt-1">
                        {maintenanceStatus.message || 'No message provided'}
                      </p>
                      {maintenanceStatus.enabledAt && (
                        <p className="text-xs text-yellow-700 mt-2">
                          Enabled: {formatDateTime(maintenanceStatus.enabledAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!maintenanceStatus?.enabled && (
                <div className="pt-4 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maintenance Message
                  </label>
                  <input
                    type="text"
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    placeholder="Enter message to display during maintenance..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-400"
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    This message will be shown to users when maintenance mode is enabled
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Detailed Stats Grid */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">System Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Teams"
            value={teamCounts?.total ?? 0}
            icon="cube"
            borderColor="blue"
            isLoading={teamsLoading}
          />
          <StatCard
            title="Active Users"
            value={userCounts?.active ?? 0}
            icon="user-check"
            borderColor="green"
            isLoading={usersLoading}
          />
          <StatCard
            title="Expired Teams"
            value={teamCounts?.expired ?? 0}
            icon={ICONS.CAUTION}
            borderColor="red"
            isLoading={teamsLoading}
          />
          <StatCard
            title="On Trial"
            value={teamCounts?.trials ?? 0}
            icon={ICONS.ALARM_CLOCK}
            borderColor="orange"
            isLoading={teamsLoading}
          />
        </div>
      </section>

      {/* Quick Actions */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <QuickActionCard
            to="/admin/users"
            title="Manage Users"
            description="View and manage all platform users"
            icon={ICONS.USERS}
            iconColor="#3b82f6"
          />
          <QuickActionCard
            to="/admin/teams"
            title="Manage Teams"
            description="View and manage all teams"
            icon={ICONS.TEAM}
            iconColor="#10b981"
          />
          <QuickActionCard
            to="/admin/packages"
            title="Packages"
            description="Manage subscription packages and pricing"
            icon="cube"
            iconColor="#8b5cf6"
          />
          <QuickActionCard
            to="/admin/system"
            title="System Health"
            description="Monitor system status and performance"
            icon={ICONS.ANALYTICS}
            iconColor="#f59e0b"
          />
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
